/**
 * A locação pelo mapa — onda 338 (RN-267). Os puros da janela «Capturar o croqui do mapa»: o giro que deita a fileira
 * de verticais na folha («Ajustar aos pontos»), o giro normalizado, a área da prancha dentro da captura e o fundo que
 * vale. As mesmas contas do servidor (`giroQueDeitaOsPontos` e o enquadramento do `svgDoCroqui`, em
 * `api/src/documentos/cptu-graficos.ts`); o 325 confere que as duas dão o mesmo.
 */

const RAD = Math.PI / 180;

/** O passo dos botões de giro, em graus (decisão do Humberto, 16/10/2026: «botões ±15°»). */
export const PASSO_DO_GIRO = 15;

/**
 * A proporção do quadro do desenho na prancha (largura ÷ altura), para a «área da prancha» da janela — a do
 * `svgDoCroqui` na caixa de 208 × 195 mm: sem giro, a moldura leva as coordenadas (17,2 mm à esquerda — 11 letras de
 * 7 pt e 2 mm —, 4,9 mm embaixo e 1,5 mm em cima e à direita); girado, 1,5 mm em volta.
 */
export function proporcaoDoQuadro(giro: number): number {
  const LETRA_MM = 7 * 0.3528;
  return Math.abs(giro) >= 0.05 ? (208 - 3) / (195 - 3)
    : (208 - 1.5 - (11 * LETRA_MM * 0.56 + 2)) / (195 - 1.5 - (LETRA_MM * 1.35 + 1.6));
}

/** O giro em (−180, 180], com duas casas. */
export function giroNormal(graus: number): number {
  if (!Number.isFinite(graus)) return 0;
  let g = graus % 360;
  if (g <= -180) g += 360;
  if (g > 180) g -= 360;
  const r = Math.round(g * 100) / 100;
  return r === -180 ? 180 : r || 0;
}

/**
 * «Ajustar aos pontos»: o giro (o `bearing`, −90 a 90) que põe a direção principal das verticais — o maior eixo da
 * dispersão, em metros no elipsoide — na horizontal da folha, com duas casas; 0 com menos de dois pontos distintos.
 */
export function giroQueDeitaOsPontos(pontos: readonly { latitude: number; longitude: number }[]): number {
  const ps = pontos.filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude));
  if (ps.length < 2) return 0;
  const lat0 = ps.reduce((s, p) => s + p.latitude, 0) / ps.length;
  const lon0 = ps.reduce((s, p) => s + p.longitude, 0) / ps.length;
  // Os metros por grau no GRS 80 (os raios do meridiano e do primeiro vertical), como o servidor (`metrosPorGrau`).
  const e2 = 0.00669438002290;
  const sen = Math.sin(lat0 * RAD);
  const w = Math.sqrt(1 - e2 * sen * sen);
  const kLat = (6378137 * (1 - e2)) / (w * w * w);
  const kLon = (6378137 / w) * Math.cos(lat0 * RAD);
  let sxx = 0; let syy = 0; let sxy = 0;
  for (const p of ps) {
    const x = (p.longitude - lon0) * RAD * kLon;
    const y = (p.latitude - lat0) * RAD * kLat;
    sxx += x * x; syy += y * y; sxy += x * y;
  }
  if (sxx + syy < 1e-18) return 0;
  const fi = (0.5 * Math.atan2(2 * sxy, sxx - syy)) / RAD;
  let g = -fi;
  if (g > 90) g -= 180;
  if (g <= -90) g += 180;
  return Math.round(g * 100) / 100 || 0;
}

/**
 * A área da prancha dentro da captura: a captura sai norte para cima (o motor), e a prancha gira o desenho — o quadro
 * da folha é o maior retângulo da proporção dada, girado pelo giro, que cabe na imagem, no centro dela. Em pixel da
 * caixa do mapa (largura × altura).
 */
export function areaDaPrancha(largura: number, altura: number, giro: number, proporcao = proporcaoDoQuadro(giro)):
  { largura: number; altura: number } {
  const c = Math.abs(Math.cos(giro * RAD));
  const s = Math.abs(Math.sin(giro * RAD));
  const h = Math.min(largura / (proporcao * c + s), altura / (proporcao * s + c));
  return { largura: proporcao * h, altura: h };
}

/** O fundo da prancha que vale: o escolhido; sem escolha, o satélite quando há croqui capturado do mapa. */
export function fundoQueVale(escolhido: 'branco' | 'satelite' | undefined, origemDoCroqui: string | null | undefined):
  'branco' | 'satelite' {
  return escolhido ?? (origemDoCroqui === 'mapa' ? 'satelite' : 'branco');
}

/** «30°», «−15°», «12,5°». */
export function textoDoGiro(g: number): string {
  const t = Number.isInteger(g) ? String(Math.abs(g)) : Math.abs(g).toFixed(1).replace('.', ',');
  return `${g < 0 ? '−' : ''}${t}°`;
}

/** «norte para cima», «girada 30° no sentido anti-horário» (o `bearing` positivo gira o desenho no anti-horário). */
export function descricaoDoGiro(g: number): string {
  if (!g) return 'norte para cima';
  return `girada ${textoDoGiro(Math.abs(g))} no sentido ${g > 0 ? 'anti-horário' : 'horário'}`;
}
